import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Crown, ArrowLeft, Loader2, CheckCircle2 } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "@/hooks/useTranslation";
import { useToast } from "@/hooks/use-toast";
import apiClient from "@/api/ApiClient";
import { useCapacitorPlatform } from "@/hooks/use-capacitor";
import { useAuth } from "@/contexts/AuthContext";
import {
  ensureRevenueCatUser,
  getRevenueCatOffers,
  purchaseRevenueCatPlan,
  type RevenueCatOfferMap,
} from "@/lib/revenueCatClient";

interface SubscriptionPaymentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedPlan: 'monthly' | 'yearly';
  onPurchaseSubscription: (plan: 'monthly' | 'yearly', paymentMethod: string) => void;
}

export const SubscriptionPaymentModal = ({
  open,
  onOpenChange,
  selectedPlan,
  onPurchaseSubscription
}: SubscriptionPaymentModalProps) => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const { toast } = useToast();
  const capacitorPlatform = useCapacitorPlatform();
  const isNative = capacitorPlatform === 'ios' || capacitorPlatform === 'android';
  const { user, refreshUserProfile } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [rcOffers, setRcOffers] = useState<RevenueCatOfferMap | null>(null);
  const [rcOffersLoading, setRcOffersLoading] = useState(false);
  const [rcOffersError, setRcOffersError] = useState<string | null>(null);
  const [purchaseSucceeded, setPurchaseSucceeded] = useState(false);

  const planDetails = {
    monthly: { price: 9.99, originalPrice: 19.99, billing: 'per month' },
    yearly: { price: 79.99, originalPrice: 159.99, billing: 'per year' }
  };

  const plan = planDetails[selectedPlan];
  // Web only: on native the store sheet is the one showing the price.
  const priceLabel = `$${plan.price}`;

  // Load RevenueCat offerings when modal opens on native platform.
  useEffect(() => {
    if (!open || !isNative) {
      // Forget the last offers so the next open starts the purchase only
      // after a fresh load (the auto-start below keys on them).
      setRcOffers(null);
      setRcOffersError(null);
      return;
    }
    let cancelled = false;
    setRcOffersLoading(true);
    setRcOffersError(null);
    (async () => {
      try {
        const offers = await getRevenueCatOffers();
        if (!cancelled) {
          setRcOffers(offers);
        }
      } catch (err) {
        console.error("[RevenueCat] Failed to load offerings:", err);
        if (!cancelled) {
          setRcOffersError(
            err instanceof Error ? err.message : "Failed to load offerings",
          );
        }
      } finally {
        if (!cancelled) setRcOffersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, isNative]);

  // Native (iOS / Android) -> RevenueCat. The store sheet is the whole
  // checkout: it shows the localized price and takes the confirmation, so
  // nothing else is shown before it. Every outcome but success closes the
  // modal (cancel silently, errors with a toast); success stays open for
  // the confirmation view.
  const purchaseFromStore = async () => {
    const offer = rcOffers?.[selectedPlan];
    if (!offer) {
      const description = rcOffersError
        ? rcOffersError
        : "Selected plan is not configured in RevenueCat. Check VITE_REVENUECAT_* keys and offerings.";
      toast({
        title: "Payment unavailable",
        description,
        variant: "destructive",
      });
      onOpenChange(false);
      return;
    }

    setIsProcessing(true);
    try {
      if (user?.id) {
        await ensureRevenueCatUser(user.id);
      }
      const result = await purchaseRevenueCatPlan(offer);
      if (result.status === "success") {
        onPurchaseSubscription(selectedPlan, 'revenuecat');
        // RC webhook updates the user on the backend; refresh profile shortly.
        setTimeout(() => {
          void refreshUserProfile();
        }, 1500);
        setPurchaseSucceeded(true);
      } else if (result.status === "cancelled") {
        console.log("[RevenueCat] Purchase cancelled by user");
        onOpenChange(false);
      } else {
        toast({
          title: "Payment Error",
          description: result.message,
          variant: "destructive",
        });
        onOpenChange(false);
      }
    } catch (err) {
      console.error("[RevenueCat] Purchase threw unexpectedly:", err);
      toast({
        title: "Payment Error",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
      onOpenChange(false);
    } finally {
      setIsProcessing(false);
    }
  };

  // Open the store sheet as soon as the offers are in, once per open.
  const storePurchaseStarted = useRef(false);
  useEffect(() => {
    if (!open) {
      storePurchaseStarted.current = false;
      return;
    }
    if (!isNative || storePurchaseStarted.current || rcOffersLoading) return;
    if (!rcOffers && !rcOffersError) return; // first load still ahead
    storePurchaseStarted.current = true;
    void purchaseFromStore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isNative, rcOffers, rcOffersLoading, rcOffersError]);

  const handleCompletePayment = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();

    console.log("handleCompletePayment called!", { isProcessing });

    if (isProcessing) {
      console.log("Already processing, ignoring click");
      return; // Prevent double clicks
    }

    setIsProcessing(true);

    // In debug mode, use Stripe (not bypass) - debug bypass is handled by special buttons
    // This function always uses Stripe, even in debug mode

    // Use Stripe Checkout for web - redirect directly to Stripe Checkout
    try {
      const successUrl = window.location.origin + "/payment/success?session_id={CHECKOUT_SESSION_ID}";
      const cancelUrl = window.location.origin + "/payment/cancel";
      
      console.log("Creating subscription checkout session...", { selectedPlan, platform: "web" });
      
      const response = await apiClient.createSubscriptionCheckout(
        selectedPlan,
        "web",
        successUrl,
        cancelUrl
      );

      console.log("Checkout session response:", response);

      if (response && response.success && response.url) {
        console.log("Redirecting to Stripe Checkout:", response.url);
        // Redirect to Stripe Checkout - don't reset isProcessing, we're leaving the page
        window.location.href = response.url;
        return; // Don't reset isProcessing, we're redirecting
      } else {
        const errorMessage = response?.message || "Failed to create checkout session. Please check Stripe configuration (Price IDs must be set in backend).";
        console.error("Failed to create subscription checkout session:", errorMessage, response);
        toast({
          title: "Payment Error",
          description: errorMessage,
          variant: "destructive",
        });
        setIsProcessing(false);
      }
    } catch (error: any) {
      console.error("Error creating subscription checkout session:", error);
      const errorMessage = error?.message || error?.response?.data?.message || "Failed to start payment. Please check backend logs and Stripe configuration.";
      toast({
        title: "Payment Error",
        description: errorMessage,
        variant: "destructive",
      });
      setIsProcessing(false);
    }
  };

  const handleClose = (open: boolean) => {
    console.log("SubscriptionPaymentModal handleClose called", { open });
    if (!open && isProcessing) {
      console.log("Modal closing while processing - this might be a problem");
    }
    if (!open) {
      setPurchaseSucceeded(false);
    }
    onOpenChange(open);
  };

  const handleSuccessContinue = () => {
    void refreshUserProfile();
    setPurchaseSucceeded(false);
    onOpenChange(false);
  };

  const SuccessView = (
    <div className="flex flex-col items-center text-center gap-4 py-8 px-4">
      <div className="w-20 h-20 rounded-full bg-emerald-500/15 flex items-center justify-center">
        <CheckCircle2 className="h-12 w-12 text-emerald-500" />
      </div>
      <div className="space-y-2">
        <h2 className="text-2xl font-bold flex items-center justify-center gap-2">
          <Crown className="h-6 w-6 text-amber-500" />
          {t('payment.subscription_active_title')}
        </h2>
        <p className="text-muted-foreground">
          {t('profile.payment_success_desc', {
            plan: selectedPlan === 'monthly'
              ? t('premium_modal.monthly_plan')
              : t('premium_modal.yearly_plan'),
          })}
        </p>
        <p className="text-sm text-muted-foreground">
          {t('premium_modal.free_tokens_100')}
        </p>
      </div>
      <Button
        type="button"
        variant="romantic"
        className="w-full"
        onClick={handleSuccessContinue}
      >
        {t('common.continue') || 'Continue'}
      </Button>
    </div>
  );

  // Native: no screen of our own before the store sheet, just a backdrop
  // with a spinner while the offers load and the sheet is up. The
  // confirmation view after a successful purchase goes through the
  // layouts below.
  if (isNative && !purchaseSucceeded) {
    if (!open) return null;
    return (
      <div className="fixed inset-0 z-[100] bg-background/80 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isMobile) {
    return (
      <>
        {/* Mobile: Full Page Payment Method Selection */}
        {open && (
          <div className="fixed inset-0 z-[100] bg-background">
            <div className="flex flex-col h-full">
              <div className="flex items-center p-4 border-b" style={{ paddingTop: "calc(1rem + var(--safe-area-inset-top, 0px))" }}>
                {!purchaseSucceeded && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onOpenChange(false)}
                    className="mr-3"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                )}
                <h1 className="text-lg font-semibold">{t('settings.complete_your_upgrade')}</h1>
              </div>

              {purchaseSucceeded ? (
                <div className="flex-1 overflow-y-auto">
                  {SuccessView}
                </div>
              ) : (
              <>
              <div className="flex-1 overflow-y-auto p-4 space-y-6">
                {/* Selected Plan Summary */}
                <Card className="p-4 bg-gradient-romantic text-white border-0">
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Crown className="h-5 w-5" />
                      <span className="text-lg font-semibold">
                        {t('premium_modal.premium')} {selectedPlan === 'monthly' ? t('premium_modal.monthly_plan') : t('premium_modal.yearly_plan')}
                      </span>
                    </div>
                    <div className="space-y-1">
                      <div className="text-sm line-through opacity-75">${plan.originalPrice}</div>
                      <div className="text-3xl font-bold">{priceLabel}</div>
                      <div className="text-sm opacity-90">{selectedPlan === 'monthly' ? t('premium_modal.per_month') : t('premium_modal.per_year')}</div>
                      <div className="text-sm font-medium text-green-200">
                        {t('premium_modal.save_50')}{selectedPlan === 'yearly' ? ' + ' + t('premium_modal.two_months_free') : ''}
                      </div>
                    </div>
                  </div>
                </Card>

                {/* Payment Method Selection */}
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-center">{t('settings.payment_method')}</h3>
                  <div className="space-y-3">
                    <Card
                      className="p-4 border-2 border-primary bg-primary/10"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-500 rounded flex items-center justify-center">
                          <span className="text-white text-sm font-bold">💳</span>
                        </div>
                        <div>
                          <h3 className="font-semibold">{t('settings.credit_debit_card')}</h3>
                          <p className="text-sm text-muted-foreground">{t('settings.pay_with_card')}</p>
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>
              </div>

              <div
                className="p-4 border-t z-50"
                style={{ paddingBottom: "calc(1rem + var(--safe-area-inset-bottom, 0px))" }}
              >
                <Button
                  type="button"
                  variant="romantic"
                  className="w-full"
                  onClick={(e) => {
                    console.log("MOBILE BUTTON CLICKED!", e);
                    e.preventDefault();
                    e.stopPropagation();
                    handleCompletePayment(e);
                  }}
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('common.loading') || 'Processing...'}
                    </>
                  ) : (
                    <>
                      <span className="mr-2">💳</span>
                      {t('settings.continue_with_card_payment')}
                    </>
                  )}
                </Button>

                <div className="text-xs text-muted-foreground text-center mt-2">
                  {t('premium_modal.secure_payment')}
                </div>
              </div>
              </>
              )}
            </div>
          </div>
        )}

      </>
    );
  }

  return (
    <>
      {/* Desktop: Payment Method Selection Modal */}
      <Dialog open={open} onOpenChange={(open) => {
        console.log("Dialog onOpenChange called directly", { open, isProcessing });
        if (!open && isProcessing) {
          console.warn("Dialog trying to close while processing - preventing close");
          return; // Don't close if processing
        }
        handleClose(open);
      }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-center gap-2 text-xl">
              <Crown className="h-6 w-6 text-amber-500" />
              {t('settings.complete_your_upgrade')}
            </DialogTitle>
          </DialogHeader>

          {purchaseSucceeded ? (
            SuccessView
          ) : (
          <div className="space-y-6">
            {/* Selected Plan Summary */}
            <Card className="p-4 bg-gradient-romantic text-white border-0">
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Crown className="h-5 w-5" />
                  <span className="text-lg font-semibold">
                    {selectedPlan === 'monthly' ? t('premium_modal.monthly') : t('premium_modal.yearly')} {t('premium_modal.premium')} {selectedPlan === 'monthly' ? t('premium_modal.monthly_plan') : t('premium_modal.yearly_plan')}
                  </span>
                </div>
                <div className="space-y-1">
                  <div className="text-sm line-through opacity-75">${plan.originalPrice}</div>
                  <div className="text-3xl font-bold">{priceLabel}</div>
                  <div className="text-sm opacity-90">{selectedPlan === 'monthly' ? t('premium_modal.per_month') : t('premium_modal.per_year')}</div>
                  <div className="text-sm font-medium text-green-200">
                    {t('premium_modal.save_50')}{selectedPlan === 'yearly' ? ' + ' + t('premium_modal.two_months_free') : ''}
                  </div>
                </div>
              </div>
            </Card>

            {/* Payment Method Selection */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-center">{t('settings.payment_method')}</h3>
              <div className="space-y-3">
                <Card
                  className="p-4 border-2 border-primary bg-primary/10"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-500 rounded flex items-center justify-center">
                      <span className="text-white text-sm font-bold">💳</span>
                    </div>
                    <div>
                      <h3 className="font-semibold">{t('settings.credit_debit_card')}</h3>
                      <p className="text-sm text-muted-foreground">{t('settings.pay_with_card')}</p>
                    </div>
                  </div>
                </Card>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Button
                type="button"
                variant="romantic"
                className="w-full"
                onClick={(e) => {
                  console.log("BUTTON CLICKED!", e);
                  e.preventDefault();
                  e.stopPropagation();
                  handleCompletePayment(e);
                }}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('common.loading') || 'Processing...'}
                  </>
                ) : (
                  <>
                    <span className="mr-2">💳</span>
                    {t('settings.continue_with_card_payment')}
                  </>
                )}
              </Button>

              <div className="text-xs text-muted-foreground text-center">
                {t('premium_modal.secure_payment')}
              </div>
            </div>
          </div>
          )}
        </DialogContent>
      </Dialog>

    </>
  );
};